<template>
  <!-- Plan 64: the "Notify me" waitlist (utils/waitlist.py), the Signups tab's Waitlist filter.
       Grouped by instrument with counts (the hiring signal). In shadow, "Would offer" rows are what
       the 11:00 run would have sent; Invite now always sends. -->
  <div class="space-y-4">
    <div class="flex flex-wrap items-center gap-2 text-xs">
      <span :class="['px-2 py-0.5 rounded-full font-medium', live ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300']">
        {{ live ? 'Live: offers are emailed' : 'Shadow: nothing is sent' }}
      </span>
      <template v-if="isAdmin">
        <button :class="btn" :disabled="busy" @click="setMode(!live)">{{ live ? 'Back to shadow' : 'Turn on offers' }}</button>
        <button :class="btn" :disabled="busy" @click="runNow">Run now</button>
      </template>
      <span v-if="message" class="text-fg-muted dark:text-fg-muted-dark">{{ message }}</span>
    </div>
    <p v-if="error" class="text-xs text-red-600 dark:text-red-400">{{ error }}</p>

    <p v-if="loaded && !open.length" class="text-sm text-fg-muted dark:text-fg-muted-dark">No one is waiting.</p>

    <section v-for="g in groups" :key="g.key">
      <h3 class="text-xs font-semibold text-fg-faint dark:text-fg-faint-dark uppercase tracking-widest mb-2">
        {{ g.label }} ({{ g.entries.length }})
      </h3>
      <div class="space-y-2">
        <div v-for="e in g.entries" :key="e.id" class="card p-3 text-sm">
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <button class="font-semibold text-fg-base dark:text-fg-base-dark hover:underline text-left" @click="openContact(e.contact_id)">{{ e.contact }}</button>
              <span v-if="e.student_name && e.student_name !== e.contact" class="text-fg-muted dark:text-fg-muted-dark"> · {{ e.student_name }}</span>
              <span class="text-fg-faint dark:text-fg-faint-dark"> · waiting since {{ fmtDate(e.line_at) }}</span>
            </div>
            <span :class="['px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0', badge(e)]">{{ badgeLabel(e) }}</span>
          </div>
          <p class="mt-1 text-fg-secondary dark:text-fg-secondary-dark">{{ summary(e) }}</p>
          <p v-if="e.note" class="mt-0.5 text-xs text-fg-muted dark:text-fg-muted-dark">{{ e.note }}</p>

          <div v-if="e.matches_now?.length" class="mt-1.5 text-xs text-fg-muted dark:text-fg-muted-dark">
            <span class="font-medium">Open now:</span>
            <span v-for="(m, i) in e.matches_now" :key="m.teacher">{{ i ? ' · ' : ' ' }}{{ m.teacher }}: {{ m.ranges.join(', ') }}</span>
          </div>
          <p v-else-if="e.status !== 'paused'" class="mt-1.5 text-xs text-fg-faint dark:text-fg-faint-dark">Nothing open that fits right now.</p>

          <ul v-if="e.offers.length" class="mt-1.5 space-y-0.5 text-xs text-fg-muted dark:text-fg-muted-dark">
            <li v-for="o in e.offers" :key="o.id">
              {{ o.shadow ? 'Would offer' : o.manual ? 'Invited' : 'Offered' }} {{ o.teacher }} {{ fmtDate(o.offered_at) }}
              <span v-if="!o.shadow">· {{ o.responded_at ? 'answered' : o.opened_at ? 'opened' : 'not opened yet' }}</span>
            </li>
          </ul>

          <div class="mt-2 flex flex-wrap gap-1.5">
            <button :class="btn" :disabled="busy" @click="editing = e">Edit</button>
            <button v-if="e.status === 'paused'" :class="btn" :disabled="busy" @click="act(e, 'restore')">Back in line</button>
            <button v-if="e.matches_now?.length" :class="primary" :disabled="busy" @click="invite(e)">{{ confirmKey === `invite${e.id}` ? 'Send offer?' : 'Invite now' }}</button>
            <button :class="btn" :disabled="busy" @click="remove(e)">{{ confirmKey === `remove${e.id}` ? 'Remove?' : 'Remove' }}</button>
          </div>
        </div>
      </div>
    </section>

    <section v-if="closed.length">
      <button class="text-xs text-fg-faint dark:text-fg-faint-dark underline" @click="showClosed = !showClosed">
        {{ showClosed ? 'Hide' : 'Show' }} recently closed ({{ closed.length }})
      </button>
      <div v-if="showClosed" class="mt-2 space-y-1 text-xs text-fg-muted dark:text-fg-muted-dark">
        <div v-for="e in closed" :key="e.id" class="flex items-center justify-between gap-2">
          <span>{{ e.contact }}<span v-if="e.instrument"> · {{ e.instrument }}</span> · {{ badgeLabel(e) }}<span v-if="e.removed_reason"> ({{ e.removed_reason }})</span></span>
          <button v-if="e.status === 'removed'" :class="btn" :disabled="busy" @click="act(e, 'restore')">Back in line</button>
        </div>
      </div>
    </section>

    <WaitlistEntryForm
      v-if="editing"
      :entry="editing"
      :title="`Waitlist: ${editing.student_name || editing.contact}`"
      :teachers="teachers"
      :reasons="reasons"
      :on-submit="saveEdit"
      @close="editing = null"
      @saved="editing = null"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useApi } from '../../composables/useApi.js'
import { useAdminSidebar } from '../../composables/useAdminSidebar.js'
import WaitlistEntryForm from './WaitlistEntryForm.vue'

defineProps({ isAdmin: { type: Boolean, default: false } })

const { fetchApi } = useApi()
const sidebar = useAdminSidebar()
const router = useRouter()

const entries = ref([])
const teachers = ref([])
const reasons = ref({})
const live = ref(false)
const loaded = ref(false)
const busy = ref(false)
const error = ref('')
const message = ref('')
const editing = ref(null)
const showClosed = ref(false)
const confirmKey = ref(null)

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const STATUS = { waiting: 'Waiting', paused: 'Paused', interested: 'Interested', booked: 'Booked', removed: 'Removed' }
const REASON_SHORT = { no_teacher: 'No teacher', no_time: 'No time', other: 'Staff invite only' }

async function load() {
  try {
    const r = await fetchApi('/api/waitlist')
    entries.value = r.entries
    teachers.value = r.teachers
    reasons.value = r.waiting_reasons
    live.value = r.live
  } catch (e) {
    error.value = e.message || 'Could not load the waitlist.'
  } finally {
    loaded.value = true
  }
}
load()

const open = computed(() => entries.value.filter(e => ['waiting', 'paused'].includes(e.status)))
const closed = computed(() => entries.value.filter(e => !['waiting', 'paused'].includes(e.status)))
const groups = computed(() => {
  const by = {}
  for (const e of open.value) {
    const key = (e.instrument || '').toLowerCase() || '~'
    ;(by[key] ||= { key, label: e.instrument || 'No instrument', entries: [] }).entries.push(e)
  }
  return Object.values(by).sort((a, b) => a.key.localeCompare(b.key))
})

const teacherName = (id) => teachers.value.find(t => t.id === id)?.name || `#${id}`
const fmtTime = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number)
  return `${h % 12 || 12}${m ? ':' + String(m).padStart(2, '0') : ''}${h < 12 ? 'am' : 'pm'}`
}
function summary(e) {
  const parts = [
    e.days.length ? e.days.map(d => DAYS[d]).join('/') : 'Any day',
    e.time_windows.length ? e.time_windows.map(w => `${fmtTime(w.start)}–${fmtTime(w.end)}`).join(', ') : 'any time',
    `${e.duration_minutes} min`,
  ]
  if (e.teacher_mode !== 'any' && e.teacher_ids.length) {
    parts.push(`${e.teacher_mode === 'only' ? 'only' : 'prefers'} ${e.teacher_ids.map(teacherName).join(', ')}`)
  }
  if (e.exclude_teacher_ids.length) parts.push(`not ${e.exclude_teacher_ids.map(teacherName).join(', ')}`)
  parts.push(REASON_SHORT[e.waiting_reason] || e.waiting_reason)
  return parts.join(' · ')
}
const badgeLabel = (e) => STATUS[e.status] || e.status
const badge = (e) => ({
  paused: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200',
  waiting: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200',
}[e.status] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300')

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' }) : ''

function openContact(contactId) {
  if (sidebar.available.value) sidebar.open('contact', { contactId })
  else router.push(`/admin/contacts/directory?contact=${contactId}`)
}

async function run(fn) {
  busy.value = true
  error.value = ''
  message.value = ''
  try {
    await fn()
    await load()
  } catch (e) {
    error.value = e.message || 'Something went wrong.'
  } finally {
    busy.value = false
  }
}

// Click once to arm, again to confirm (3s), like the card's other irreversible buttons.
let confirmTimer = null
function armed(key) {
  if (confirmKey.value === key) {
    confirmKey.value = null
    return true
  }
  confirmKey.value = key
  clearTimeout(confirmTimer)
  confirmTimer = setTimeout(() => { confirmKey.value = null }, 3000)
  return false
}

const post = (path, body = {}) => fetchApi(path, { method: 'POST', body: JSON.stringify(body) })
const act = (e, action) => run(() => post(`/api/waitlist/${e.id}/${action}`))
const invite = (e) => armed(`invite${e.id}`) && run(async () => {
  await post(`/api/waitlist/${e.id}/invite`)
  message.value = `Offer sent to ${e.contact}.`
})
const remove = (e) => armed(`remove${e.id}`) && run(() => post(`/api/waitlist/${e.id}/remove`))
const setMode = (on) => run(() => fetchApi('/api/waitlist/mode', { method: 'PUT', body: JSON.stringify({ live: on }) }))
const runNow = () => run(async () => {
  const r = await post('/api/waitlist/run')
  message.value = `${r.new} new ${live.value ? 'offer' : 'would-offer'}${r.new === 1 ? '' : 's'}${r.sent ? `, ${r.sent} sent` : ''}.`
})

async function saveEdit(params) {
  await fetchApi(`/api/waitlist/${editing.value.id}`, { method: 'PUT', body: JSON.stringify(params) })
  await load()
}

const btn = 'px-2.5 py-1 text-xs font-medium rounded-md border border-stroke-strong dark:border-stroke-strong-dark text-fg-secondary dark:text-fg-secondary-dark disabled:opacity-50'
const primary = 'px-2.5 py-1 text-xs font-medium rounded-md bg-blue-600 text-white disabled:opacity-50'
</script>
